<script setup lang="ts">
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    ZhEmptyState,
    ZhPageTitle,
    ZhStatusPill,
    ZhUsageMeter,
} from '@zerohuman/ui'
import { onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { api, type StaffEnterpriseRow } from '@/api'
import { billingWords, dayOf, planSourceWords } from '@/lib/staffEnterprises'

/**
 * The Staff area's enterprises (#394): every enterprise on one page, closed ones included, with its plan, how the plan
 * was set, its billing state and this period's usage against the plan's limits. A row opens the enterprise's page,
 * where staff act on it. Only a platform admin is offered this page, and the API refuses everybody else.
 */
const rows = ref<StaffEnterpriseRow[]>([])
const loading = ref(true)
const error = ref<string | null>(null)

onMounted(async () => {
    try {
        rows.value = await api.staffEnterprises()
    } catch (failure) {
        error.value = failure instanceof Error ? failure.message : 'Could not load the enterprises.'
    } finally {
        loading.value = false
    }
})
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<ZhPageTitle kicker="Staff">Enterprises</ZhPageTitle>
		<p class="text-muted-foreground text-sm">
			Every enterprise's plan, billing and usage this period. Open one to set its plan, extend its period, pause it
			or close it. No tenant's runs, memory, mail or secrets are shown here.
		</p>
		<p v-if="error" class="text-destructive text-sm" role="alert" data-testid="staff-enterprises-error">{{ error }}</p>
		<Table v-if="rows.length">
			<TableHeader>
				<TableRow>
					<TableHead>Enterprise</TableHead>
					<TableHead>Plan</TableHead>
					<TableHead>Billing</TableHead>
					<TableHead>Portal runs</TableHead>
					<TableHead>Runner minutes</TableHead>
					<TableHead>Members</TableHead>
					<TableHead>State</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				<TableRow v-for="row in rows" :key="row.id" data-testid="staff-enterprise-row">
					<TableCell>
						<RouterLink :to="`/staff/enterprises/${row.id}`" class="font-medium underline underline-offset-4">
							{{ row.name }}
						</RouterLink>
					</TableCell>
					<TableCell>
						<div>{{ row.plan.name }}</div>
						<div class="text-muted-foreground text-xs">{{ planSourceWords(row.planSource) }}</div>
					</TableCell>
					<TableCell>{{ billingWords(row.billing) }}</TableCell>
					<TableCell>
						<ZhUsageMeter :used="row.usage.portalRuns.used" :included="row.usage.portalRuns.limit" unit="runs" />
					</TableCell>
					<TableCell>
						<ZhUsageMeter
							:used="row.usage.runnerMinutes.used"
							:included="row.usage.runnerMinutes.limit"
							unit="minutes"
						/>
					</TableCell>
					<TableCell>
						<ZhUsageMeter :used="row.usage.members.used" :included="row.usage.members.limit" unit="members" />
					</TableCell>
					<TableCell>
						<ZhStatusPill v-if="row.closed" tone="failed" :pulse="false">
							Closed, erased {{ dayOf(row.closed.purgeAt) || 'never' }}
						</ZhStatusPill>
						<ZhStatusPill v-else-if="row.paused" tone="action" :pulse="false">Paused</ZhStatusPill>
						<ZhStatusPill v-else tone="completed" :pulse="false">Open</ZhStatusPill>
					</TableCell>
				</TableRow>
			</TableBody>
		</Table>
		<ZhEmptyState v-else-if="!loading && !error">No enterprises yet.</ZhEmptyState>
	</section>
</template>
